import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { listExpected, listPasses } from '../../../../api/visitor-passes-client';
import type { ExpectedRow, VisitorPass } from '../../../../interfaces/visitor-pass';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { Collapse } from '../../../../motion';
import { clockTime } from '../../../../function-utils/shift-time';
import { formatVisitDay } from '../../../../utils/pass-schedule';
import {
  expectedHrefFor,
  guestNames,
  matchPasses,
  signableRowFor,
} from '../../../../utils/pass-match';
import { palette, radius, spacing, status, typography } from '../../../../style-utils/theme';
import { InlineAction } from './id';

/**
 * THE WALK-IN BANNER (resident-portal Plan 3, RP-3.9) — on the wizard's
 * Apartment/Location step.
 *
 * The desk is typing a visitor the ordinary way. Once a unit is chosen, the
 * unit's LIVE passes are read (GET /buildings/:b/passes?state=live&unit_id=…,
 * not today's list alone: only the pass list can say "has a pass, but not
 * now") and matched against the guest names already typed, in the browser
 * (utils/pass-match — names have no blind index, F-9).
 *
 *   signable now  brand banner: "Maria Lopez has a pass for 1102 today,
 *                 09:00–13:00." + "Use Maria's pass" → the Expected screen with
 *                 her row's confirm open. ONE confirm UI on the desk.
 *   not now       neutral banner, no action: "Maria has a pass for 1102 on
 *                 Tuesdays, not now. Ring up as usual." (RP-D5)
 *
 * It never picks for the desk (two passes with the same first name are both
 * listed, each with its own action), never blocks Continue, and ignoring it
 * leaves the wizard exactly as it was. It enters and leaves in Collapse; the
 * step itself does not move.
 *
 * A PASS STILL WAITING FOR THE BUILDING'S APPROVAL IS NOT ANNOUNCED (RP-9.7).
 * The register's "live" list includes it (a manager has to see it to answer
 * it), but nobody is expected on it yet: "Maria has a pass" would tell the
 * desk something the building has not agreed to.
 */

/** A pass the desk may be told about: approved, or never needing approval. */
export const isAnnounceable = (pass: Pick<VisitorPass, 'status' | 'state'>): boolean =>
  pass.status !== 'pending' && pass.state !== 'pending';

const WEEKDAYS_PLURAL = ['Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays', 'Sundays'];

const whenPhrase = (pass: VisitorPass, today: string | null): string => {
  if (pass.schedule_kind === 'once') return `on ${formatVisitDay(pass.valid_from, today)}`;
  const days = (pass.days || []).filter(d => d >= 1 && d <= 7);
  if (days.length === 7) return 'every day';
  const names = days.map(d => WEEKDAYS_PLURAL[d - 1]);
  const list = names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  const every = Number(pass.interval_weeks) > 1 ? ` (every ${pass.interval_weeks} weeks)` : '';

  return `on ${list || 'set days'}${every}`;
};

const windowOf = (row: ExpectedRow, zone: string): string => {
  if (row.all_day) return 'any time';
  const from = clockTime(row.starts_at, zone);
  const to = clockTime(row.ends_at, zone);

  return from && to ? `${from}–${to}` : 'today';
};

const fullName = (pass: Pick<VisitorPass, 'first_name' | 'last_name'>): string =>
  [pass.first_name, pass.last_name].filter(Boolean).join(' ') || 'This visitor';

interface Match {
  pass: VisitorPass;
  row: ExpectedRow | null;
}

export interface WalkInPassBannerProps {
  buildingId: number | null;
  unitId: number | null;
  /** The unit as the desk sees it ("1102"). */
  unitLabel: string;
  names: readonly string[];
  lastNames: readonly string[];
  /** Go to the Expected screen with this pass's confirm open. */
  onUse: (href: string) => void;
}

export const WalkInPassBanner: React.FC<WalkInPassBannerProps> = ({
  buildingId,
  unitId,
  unitLabel,
  names,
  lastNames,
  onUse,
}) => {
  const { permissions } = useAuth();
  const mayRead = can(permissions, PERMISSIONS.VISITORPASS_READ);
  const guests = useMemo(() => guestNames(names, lastNames), [names, lastNames]);
  const guestKey = guests.map(g => `${g.first}|${g.last}`).join(',');
  const [matches, setMatches] = useState<Match[]>([]);
  const [zone, setZone] = useState('');
  const [today, setToday] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    seq.current += 1;
    const mine = seq.current;
    setMatches([]);
    if (!mayRead || !buildingId || !unitId || !guests.length) return undefined;

    (async () => {
      try {
        const list = await listPasses(buildingId, { state: 'live', unitId, limit: 100 });
        if (seq.current !== mine || list.passes_enabled === false) return;
        const found = matchPasses(list.rows.filter(isAnnounceable), guests);
        if (!found.length) return;
        // Today's list decides "now": the server's window state and the party.
        let rows: ExpectedRow[] = [];
        let deskZone = list.zone || '';
        try {
          const expected = await listExpected(buildingId);
          rows = expected.passes_enabled ? expected.rows : [];
          deskZone = expected.desk_day?.zone || deskZone;
        } catch {
          rows = [];
        }
        if (seq.current !== mine) return;
        setZone(deskZone);
        setToday(list.today || null);
        setMatches(found.map(pass => ({ pass, row: signableRowFor(pass.pass_id, rows) })));
      } catch {
        // A banner that cannot be read is no banner: the wizard goes on as
        // it always has, and the desk rings up.
        if (seq.current === mine) setMatches([]);
      }
    })();

    return () => {
      seq.current += 1;
    };
    // guestKey stands in for `guests`: the same names re-typed are the same
    // question, and must not refetch.
  }, [mayRead, buildingId, unitId, guestKey]);

  return (
    <Collapse open={matches.length > 0} id="walk-in-pass-banner">
      <Stack role="status" aria-label="Visitor passes for this unit">
        {matches.map(({ pass, row }) => {
          const first = pass.first_name || fullName(pass);

          return row ? (
            <Note key={pass.pass_id} $tone="brand">
              <span>
                {fullName(pass)} has a pass for {unitLabel || 'this unit'} today, {windowOf(row, zone)}.
              </span>
              <InlineAction type="button" onClick={() => onUse(expectedHrefFor(pass.pass_id))}>
                Use {first}&apos;s pass
              </InlineAction>
            </Note>
          ) : (
            <Note key={pass.pass_id} $tone="neutral">
              <span>
                {first} has a pass for {unitLabel || 'this unit'} {whenPhrase(pass, today)}, not now. Ring up as
                usual.
              </span>
            </Note>
          );
        })}
      </Stack>
    </Collapse>
  );
};

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

/* status.brand / status.neutral triples: the text is the tone's own fg, which
   meets contrast on its tint (theme.ts). */
const Note = styled.p<{ $tone: 'brand' | 'neutral' }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs} ${spacing.sm};
  margin: 0;
  padding: ${spacing.sm} ${spacing.md};
  border-radius: ${radius.md};
  border: 1px solid ${({ $tone }) => ($tone === 'brand' ? palette.iris : palette.line)};
  background: ${({ $tone }) => status[$tone].bg};
  color: ${({ $tone }) => status[$tone].fg};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  overflow-wrap: anywhere;
`;
